import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import SearchBar from './components/SearchBar';
import Pagination from '../../components/base/Pagination';

interface Listing {
  id: string;
  name: string;
  category: string;
  location: string;
  rating: number;
  review_count: number;
  verified: boolean;
  description: string;
  images: string[];
}

export default function DirectoryPage() {
  // Add scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [searchParams] = useSearchParams();
  const [scrolled, setScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredListings, setFilteredListings] = useState<Listing[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchListings();
  }, []);

  // Handle URL parameters for category filtering
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  useEffect(() => {
    let filtered = [...listings];

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(listing => listing.category === selectedCategory);
    }

    // Apply location filter
    if (selectedLocation !== 'All') {
      filtered = filtered.filter(listing => listing.location === selectedLocation);
    }

    // Apply verified filter
    if (verifiedOnly) {
      filtered = filtered.filter(listing => listing.verified);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(listing => 
        listing.name.toLowerCase().includes(query) ||
        listing.description?.toLowerCase().includes(query) ||
        listing.category.toLowerCase().includes(query) ||
        listing.location.toLowerCase().includes(query)
      );
    }

    setFilteredListings(filtered);
  }, [listings, selectedCategory, selectedLocation, verifiedOnly, searchQuery]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('status', 'approved')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', 'Legal', 'Finance', 'Housing', 'Pets', 'Moving', 'Healthcare', 'Education', 'Transportation'];
  const locations = ['All', 'Dublin', 'Cork', 'Galway', 'Limerick', 'Nationwide'];

  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentListings = filteredListings.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedLocation, verifiedOnly, searchQuery]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />

      {/* Header */}
      <section className="pt-32 pb-16 px-6 bg-[#0A381F] text-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Service Directory</h1>
          <p className="text-xl text-white/80 max-w-3xl mb-8">Find verified experts to help with every aspect of your move to Ireland</p>
          
          {/* Search Bar */}
          <div className="max-w-2xl">
            <SearchBar 
              onSearch={handleSearch}
              placeholder="Search immigration lawyers, housing agents, pet movers..."
            />
          </div>
        </div>
      </section>

      {/* Directory Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h3 className="text-xl font-bold text-[#0A381F] mb-6">Filters</h3>
              
              {/* Category Filter */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-700 mb-3">Category</h4>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-[#0A381F] text-white'
                          : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Filter */}
              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-700 mb-3">Location</h4>
                <div className="space-y-2">
                  {locations.map((location) => (
                    <button
                      key={location}
                      onClick={() => setSelectedLocation(location)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        selectedLocation === location
                          ? 'bg-[#0A381F] text-white'
                          : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {location}
                    </button>
                  ))}
                </div>
              </div>

              {/* Verified Filter */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-[#0A381F] focus:ring-[#D4AF37] cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-700">Verified Only</span>
                </label>
              </div>
            </div>
          </aside>

          {/* Listings Grid */}
          <div className="flex-1">
            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing <strong>{currentListings.length}</strong> of <strong>{filteredListings.length}</strong> providers
                {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-12 h-12 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin"></div>
              </div>
            ) : currentListings.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6">
                  <i className="ri-search-line text-4xl text-gray-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F] mb-3">No providers found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your filters or search query</p>
                <button
                  onClick={() => {
                    setSelectedCategory('All');
                    setSelectedLocation('All');
                    setVerifiedOnly(false);
                    setSearchQuery('');
                  }}
                  className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg font-semibold hover:bg-[#C19B2F] transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentListings.map((listing) => (
                    <div key={listing.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                      <div className="relative w-full h-48">
                        <img
                          src={listing.images?.[0] || 'https://public.readdy.ai/ai/img_res/3cd3270319ca66d2a6162c6e87373a3a.jpg'}
                          alt={listing.name}
                          className="w-full h-full object-cover object-top"
                        />
                        {listing.verified && (
                          <div className="absolute top-4 right-4 bg-[#D4AF37] text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-verified-badge-fill text-sm"></i>
                            </div>
                            Verified
                          </div>
                        )}
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-[#0A381F] mb-1">{listing.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <span>{listing.category}</span>
                              <span>•</span>
                              <span>{listing.location}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mb-4">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className="w-4 h-4 flex items-center justify-center">
                                <i className={`ri-star-${i < Math.floor(listing.rating) ? 'fill' : 'line'} text-[#D4AF37] text-base`}></i>
                              </div>
                            ))}
                          </div>
                          <span className="text-sm font-semibold text-[#0A381F]">{listing.rating}</span>
                          <span className="text-sm text-gray-500">({listing.review_count} reviews)</span>
                        </div>

                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{listing.description}</p>

                        <Link
                          to={`/directory/${listing.id}`}
                          className="block w-full text-center bg-[#0A381F] hover:bg-[#D4AF37] text-white py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-12">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
